import { XUIPanelSection } from '@xero/xui/react/panel';
import { XUIPickitem } from '@xero/xui/react/picklist';
import { ChangeEvent, Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { DropDownSelect } from '../../../../components/DropDownSelect';
import { InfoPopover } from '../../../../components/InfoPopover';
import Link from '../../../../components/Link';
import { createButtonContent, formatPickListItem } from '../../../../helpers/accountDropdown';
import { AccountSettingType, IAccount, IShopifySettingsPutRequest, IShopifySettingsResponse } from '../../../../models';
import { settingsSelectors } from '../../../../redux/slices/SettingsSlice';
import './ExpensesSetupTab.css';

interface IExpensesSetupTabProps {
    integrationLang: Record<string, any>;
    onSetFees: (feesAccountId: string) => void;
    onSetTaxes: (taxesAccountId: string) => void;
    onSetGiftCards: (giftCardsAccountId: string) => void;
    onValid: (isValid: boolean) => void;
}

export const ExpensesSetupTab: FC<IExpensesSetupTabProps> = ({
    integrationLang,
    onSetFees: onSetFees,
    onSetTaxes,
    onSetGiftCards,
    onValid,
}) => {
    const receivedSettings = useSelector(settingsSelectors.settings) as IShopifySettingsResponse;
    const settings = useSelector(settingsSelectors.settings) as IShopifySettingsPutRequest;

    const feesAccountSettings = receivedSettings?.chartOfAccountsSettings.find(
        (f) => f.accountSettingType === AccountSettingType.Fees
    );
    const feesAccountEligible =
        receivedSettings?.chartOfAccounts.filter((f) => feesAccountSettings?.eligibleAccounts.includes(f.accountId)) ??
        [];
    const taxAccountSettings = receivedSettings?.chartOfAccountsSettings.find(
        (f) => f.accountSettingType === AccountSettingType.Tax
    );
    const taxAccountEligible =
        receivedSettings?.chartOfAccounts.filter((f) => taxAccountSettings?.eligibleAccounts.includes(f.accountId)) ??
        [];
    const giftCardsAccountSettings = receivedSettings?.chartOfAccountsSettings.find(
        (f) => f.accountSettingType === AccountSettingType.GiftCards
    );
    const giftCardsAccountEligible =
        receivedSettings?.chartOfAccounts.filter((f) =>
            giftCardsAccountSettings?.eligibleAccounts.includes(f.accountId)
        ) ?? [];

    const clearInputSearch = (setState: Dispatch<SetStateAction<string>>) => setState('');

    const [feesSearch, setFeesSearch] = useState('');
    const handleFeesChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setFeesSearch(event.target.value);
    const handleFeesOnBlur = () => {
        clearInputSearch(setFeesSearch);
    };
    const handleFeesSelected = (account: IAccount) => () => {
        clearInputSearch(setFeesSearch);
        onSetFees(account.accountId);
    };

    const [taxesSearch, setTaxesSearch] = useState('');
    const handleTaxesChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setTaxesSearch(event.target.value);
    const handleTaxesOnBlur = () => {
        clearInputSearch(setTaxesSearch);
    };
    const handleTaxesSelected = (account: IAccount) => () => {
        clearInputSearch(setTaxesSearch);
        onSetTaxes(account.accountId);
    };

    const [giftCardsSearch, setGiftCardsSearch] = useState('');
    const handleGiftCardsChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setGiftCardsSearch(event.target.value);
    const handleGiftCardOnBlur = () => {
        clearInputSearch(setGiftCardsSearch);
    };
    const handleGiftCardSelected = (account: IAccount) => () => {
        clearInputSearch(setGiftCardsSearch);
        onSetGiftCards(account.accountId);
    };

    const DropDownButton = createButtonContent(receivedSettings?.chartOfAccounts!);

    useEffect(() => {
        onValid(true);
    }, [settings, onValid]);

    return (
        <>
            <XUIPanelSection className='shopify-panel--section' heading={integrationLang.SETUP_PAGE.EXPENSES_TAB.TITLE}>
                <div className='content shopify-content xui-padding-bottom-3xlarge'>
                    <div className='shopify-panel--description xui-padding-top-xlarge'>
                        {integrationLang.SETUP_PAGE.EXPENSES_TAB.BLURB}
                    </div>
                    <DropDownSelect
                        buttonContent={
                            <DropDownButton
                                accountId={settings.feesAccountId}
                                defaultAccount={feesAccountSettings!}
                                setAccount={onSetFees}
                            >
                                {integrationLang.SETUP_PAGE.EXPENSES_TAB.SELECT_ACCOUNT}
                            </DropDownButton>
                        }
                        buttonLabel={
                            <div>
                                {integrationLang.SETUP_PAGE.EXPENSES_TAB.SHOPIFY_FEES}{' '}
                                <InfoPopover>{integrationLang.SETUP_PAGE.EXPENSES_TAB.SHOPIFY_FEES_HINT}</InfoPopover>
                            </div>
                        }
                        showSearchBox={true}
                        showFooter={false}
                        onBlur={handleFeesOnBlur}
                        onInputChange={handleFeesChange}
                        qaHook='fees-account-dropdown'
                    >
                        {[
                            ...(feesAccountSettings && feesAccountSettings.defaultToSuggestion
                                ? [feesAccountSettings.suggestedAccount]
                                : []),
                            ...feesAccountEligible,
                        ]
                            .filter(
                                (f) =>
                                    feesSearch === '' ||
                                    `${f.code}-${f.name}`.toLowerCase().includes(feesSearch.toLowerCase())
                            )
                            .map((expenseAccount, i) => (
                                <XUIPickitem
                                    id={String(i + 1)}
                                    key={i + 1}
                                    onClick={handleFeesSelected(expenseAccount)}
                                    isSelected={settings.feesAccountId === expenseAccount.accountId}
                                >
                                    {formatPickListItem(expenseAccount)}
                                </XUIPickitem>
                            ))}
                    </DropDownSelect>
                    <DropDownSelect
                        buttonContent={
                            <DropDownButton
                                accountId={settings.taxCollectedAccountId}
                                defaultAccount={taxAccountSettings!}
                                setAccount={onSetTaxes}
                                isLiability={true}
                            >
                                {integrationLang.SETUP_PAGE.EXPENSES_TAB.SELECT_ACCOUNT}
                            </DropDownButton>
                        }
                        buttonLabel={<div>{integrationLang.SETUP_PAGE.EXPENSES_TAB.TAX_COLLECTED} </div>}
                        showSearchBox={true}
                        showFooter={false}
                        onBlur={handleTaxesOnBlur}
                        onInputChange={handleTaxesChange}
                        qaHook='taxes-account-dropdown'
                    >
                        {[
                            ...(taxAccountSettings && taxAccountSettings.defaultToSuggestion
                                ? [taxAccountSettings.suggestedAccount]
                                : []),
                            ...taxAccountEligible,
                        ]
                            .filter(
                                (f) =>
                                    taxesSearch === '' ||
                                    `${f.code}-${f.name}`.toLowerCase().includes(taxesSearch.toLowerCase())
                            )
                            .map((liabilityAccount, i) => (
                                <XUIPickitem
                                    id={String(i + 1)}
                                    key={i + 1}
                                    onClick={handleTaxesSelected(liabilityAccount)}
                                    isSelected={settings.taxCollectedAccountId === liabilityAccount.accountId}
                                >
                                    {formatPickListItem(liabilityAccount)}
                                </XUIPickitem>
                            ))}
                    </DropDownSelect>
                    <DropDownSelect
                        buttonContent={
                            <DropDownButton
                                accountId={settings.giftCardsAccountId}
                                defaultAccount={giftCardsAccountSettings!}
                                setAccount={onSetGiftCards}
                                isLiability={true}
                            >
                                {integrationLang.SETUP_PAGE.EXPENSES_TAB.SELECT_ACCOUNT}
                            </DropDownButton>
                        }
                        buttonLabel={
                            <div>
                                {integrationLang.SETUP_PAGE.EXPENSES_TAB.GIFTCARDS}{' '}
                                <InfoPopover>{integrationLang.SETUP_PAGE.EXPENSES_TAB.GIFTCARDS_HINT}</InfoPopover>
                            </div>
                        }
                        showSearchBox={true}
                        showFooter={false}
                        onBlur={handleGiftCardOnBlur}
                        onInputChange={handleGiftCardsChange}
                        qaHook='giftcards-account-dropdown'
                    >
                        {[
                            ...(giftCardsAccountSettings && giftCardsAccountSettings.defaultToSuggestion
                                ? [giftCardsAccountSettings.suggestedAccount]
                                : []),
                            ...giftCardsAccountEligible,
                        ]
                            .filter(
                                (f) =>
                                    giftCardsSearch === '' ||
                                    `${f.code}-${f.name}`.toLowerCase().includes(giftCardsSearch.toLowerCase())
                            )
                            .map((liabilityAccount, i) => (
                                <XUIPickitem
                                    key={i + 1}
                                    id={String(i + 1)}
                                    onClick={handleGiftCardSelected(liabilityAccount)}
                                    isSelected={settings.giftCardsAccountId === liabilityAccount.accountId}
                                >
                                    {formatPickListItem(liabilityAccount)}
                                </XUIPickitem>
                            ))}
                    </DropDownSelect>
                    <div className='xui-padding-top-xlarge xui-text-align-center xui-stepper-link-description'>
                        {integrationLang.SETUP_PAGE.EXPENSES_TAB.COA_HELP_PREAMBLE}
                        <Link
                            displayLinkIcon={true}
                            href={integrationLang.SETUP_PAGE.EXPENSES_TAB.COA_HELP_LINK}
                        >
                            {integrationLang.SETUP_PAGE.EXPENSES_TAB.COA_HELP_TEXT}
                        </Link>
                    </div>
                </div>
            </XUIPanelSection>
        </>
    );
};
